import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { parseProblem, problemMessage } from '../http/api-problem';
import { PENDING_DELETION_URL } from './auth.guard';
import { AuthStore } from './auth.store';

/**
 * Un 401 su una richiesta protetta vuol dire che la sessione non c'è più (scaduta, revocata,
 * chiusa in un'altra scheda): si svuota lo stato e si va all'accesso con `returnUrl`. Con
 * `session_expired` la pagina di accesso spiega perché.
 *
 * Le richieste di `/api/auth/` sono escluse: un 401 dall'accesso è una password sbagliata, e da
 * `/api/auth/me` all'avvio è la risposta normale di chi non è autenticato.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const store = inject(AuthStore);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      // Sessione limitata (FR-092): l'account è in attesa di eliminazione, resta solo la scelta
      // fra annullare l'eliminazione e uscire.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        !request.url.startsWith('/api/auth/') &&
        parseProblem(error).code === 'account_pending_deletion'
      ) {
        store.patch({ status: 'pending_deletion' });
        if (!router.url.startsWith(PENDING_DELETION_URL)) {
          void router.navigateByUrl(PENDING_DELETION_URL);
        }
        return throwError(() => error);
      }
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !request.url.startsWith('/api/auth/')
      ) {
        const problem = parseProblem(error);
        if (problem.code === 'session_expired') {
          store.setNotice(problemMessage(problem));
        }
        store.clear();
        const current = router.url;
        if (!current.startsWith('/login')) {
          void router.navigate(['/login'], {
            queryParams: current && current !== '/' ? { returnUrl: current } : {},
          });
        }
      }
      return throwError(() => error);
    }),
  );
};
